<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="analyte-server-table"
      class="w-100"
    >
      <div v-if="!sample_type_id" slot="beforeTable" class="pt-3">
        <select-filter
          variant="info"
          :icon="sample_type.icon"
          placeholder="All Sample Types"
          :collection="sample_type.filters"
          value-key="id"
          text-key="name"
          filter-field="sample_type_id"
          filter-operator="eq"
          @onSelect="setCustomFilter('sample_type', $event)"
        />
      </div>
      <span slot="sample_type.name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['sample_type']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/sampletypes/${getData(row, {
                levels: ['sample_type'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["sample_type"], field: "name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="name" slot-scope="{ row }">
        <a :href="`#/analytes/${row.id}`" class="btn-link">
          {{ formatAnalyteName(row) }}
        </a>
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit analyte')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/analytes/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete analyte')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import AnalyteCollection from "@/collections/lab/AnalyteCollection";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import SelectFilter from "@/components/shared/SelectFilter";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData, formatAnalyteName } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "AnalyteServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ServerTable,
    SelectFilter,
    ModalTrashBasket,
  },
  props: {
    collection: {
      type: Object,
      default: () => new AnalyteCollection(),
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "sample_type.name",
        "name",
        "modus_name",
        "abbrev",
        "description",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => ["sample_type.name", "name", "modus_name", "abbrev"],
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit analyte") ||
      this.checkPermission("delete analyte");

    return {
      entity_type: "analyte",
      sample_type_id: this.$route.params.sample_type_id || null,
      sample_type_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            "sample_type.name": "Sample Type",
            name: "Analyte Name",
            modus_name: "MODUS",
          },
        },
      }),
    };
  },
  computed: {
    ...mapGetters({
      trash: "analyteTrash",
    }),
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete analyte") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    formatAnalyteName(analyte) {
      return formatAnalyteName(analyte);
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async init() {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
    },
    prepareCollectionFilter(filter) {
      this.sample_type_id &&
        filter.filterEquals("sample_type_id", this.sample_type_id);

      return filter;
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
  },
  created() {
    this.init();
  },
};
</script>
